import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Eye, Pencil, KeyRound, Ban, CheckCircle2, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Avatar from "@/components/Avatar";
import EmptyState from "@/components/EmptyState";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY_FORM = { company_name: "", contact_person: "", email: "", phone: "", address: "", username: "", password: "", status: "active" };

export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("active");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addClient = async () => {
    if (!form.company_name.trim() || !form.contact_person.trim() || !form.email.trim() || !form.password) {
      toast.error("Company name, contact person, email and password are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/clients", { ...form, username: form.username.trim() || undefined });
      toast.success(`Client "${form.company_name}" created.`);
      setAddOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${editTarget.id}`, editForm);
      toast.success("Client updated.");
      setEditTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const doResetPassword = async () => {
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${resetTarget.id}/reset-password`, { password: newPassword });
      toast.success(`Password reset for ${resetTarget.company_name}.`);
      setResetTarget(null);
      setNewPassword("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const doToggle = async () => {
    const next = toggleTarget.status === "active" ? "inactive" : "active";
    try {
      await api.post(`/clients/${toggleTarget.id}/status`, { status: next });
      toast.success(`Client ${next === "active" ? "activated" : "deactivated"}.`);
      setToggleTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/clients/${deleteTarget.id}`);
      toast.success(`Client "${deleteTarget.company_name}" permanently deleted.`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const activeCount = clients.filter((c) => c.status === "active").length;
  const visibleClients = clients.filter((c) => (tab === "active" ? c.status === "active" : c.status !== "active"));

  return (
    <div className="space-y-5" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="clients-title">Clients</h1>
          <p className="text-sm text-slate-500 mt-1">Manage client companies and their portal logins.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-client-button" className="bg-primary hover:bg-[#152D53] rounded-sm">
          <Plus className="h-4 w-4 mr-2" /> Add Client
        </Button>
      </div>

      <div className="flex gap-1 bg-white border border-slate-200 rounded-md p-1 w-fit" data-testid="clients-tabs">
        <button
          onClick={() => setTab("active")}
          data-testid="clients-tab-active"
          className={`px-4 py-1.5 rounded-sm text-xs font-semibold transition-colors duration-150 ${tab === "active" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}
        >
          Active ({activeCount})
        </button>
        <button
          onClick={() => setTab("inactive")}
          data-testid="clients-tab-inactive"
          className={`px-4 py-1.5 rounded-sm text-xs font-semibold transition-colors duration-150 ${tab === "inactive" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}
        >
          Inactive ({clients.length - activeCount})
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded-sm animate-pulse" />)}</div>
        ) : visibleClients.length === 0 ? (
          <EmptyState title={tab === "active" ? "No active clients" : "No inactive clients"} message={tab === "active" ? "Add your first client company to start creating orders." : "Deactivated clients will appear here and can be permanently deleted."} testId="clients-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {["Company", "Contact Person", "Email", "Username", "Phone", "Orders", "Status", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleClients.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 even:bg-slate-50/60 hover:bg-blue-50/40 transition-colors duration-100" data-testid={`client-row-${c.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={c.contact_person} userId={c.user_id} avatarPath={c.avatar_path} size="h-8 w-8" textSize="text-xs" testId={`client-avatar-${c.id}`} />
                        <span className="font-semibold text-slate-900">{c.company_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{c.contact_person}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{c.email}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{c.username || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap tnum">{c.phone || "—"}</td>
                    <td className="px-4 py-3 text-slate-800 tnum">{c.order_count}</td>
                    <td className="px-4 py-3">
                      <span data-testid={`client-status-${c.id}`} className={`px-2 py-0.5 rounded-sm text-xs font-medium border ${c.status === "active" ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                        {c.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button title="View orders" data-testid={`client-orders-${c.id}`} onClick={() => navigate(`/orders?client_id=${c.id}`)} className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"><Eye className="h-4 w-4" /></button>
                        <button title="Edit" data-testid={`client-edit-${c.id}`} onClick={() => { setEditTarget(c); setEditForm({ company_name: c.company_name, contact_person: c.contact_person, email: c.email, phone: c.phone || "", address: c.address || "" }); }} className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"><Pencil className="h-4 w-4" /></button>
                        <button title="Reset password" data-testid={`client-reset-pw-${c.id}`} onClick={() => setResetTarget(c)} className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"><KeyRound className="h-4 w-4" /></button>
                        <button title={c.status === "active" ? "Deactivate" : "Activate"} data-testid={`client-toggle-${c.id}`} onClick={() => setToggleTarget(c)} className={`p-1.5 rounded-sm transition-colors duration-150 ${c.status === "active" ? "text-slate-500 hover:text-[#E63946] hover:bg-[#E63946]/10" : "text-slate-500 hover:text-green-600 hover:bg-green-50"}`}>
                          {c.status === "active" ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                        </button>
                        {c.status !== "active" && (
                          <button title="Delete permanently" data-testid={`client-delete-${c.id}`} onClick={() => setDeleteTarget(c)} className="p-1.5 rounded-sm text-slate-500 hover:text-[#E63946] hover:bg-[#E63946]/10 transition-colors duration-150">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-client-dialog">
          <DialogHeader><DialogTitle>Add Client</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="space-y-2"><Label>Company Name *</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="add-client-company" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Contact Person *</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="add-client-contact" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Email (login) *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-client-email" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="add-client-username" placeholder="Defaults to email prefix" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Phone Number</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="add-client-phone" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Temporary Password *</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="add-client-password" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="add-client-address" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Account Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="add-client-status" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="add-client-cancel" className="rounded-sm">Cancel</Button>
            <Button onClick={addClient} disabled={saving} data-testid="add-client-submit" className="bg-primary hover:bg-[#152D53] rounded-sm">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Client"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={Boolean(editTarget)} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-2xl" data-testid="edit-client-dialog">
          <DialogHeader><DialogTitle>Edit Client</DialogTitle></DialogHeader>
          {editForm && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <div className="space-y-2"><Label>Company Name</Label><Input value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} data-testid="edit-client-company" className="border-slate-300 rounded-sm" /></div>
              <div className="space-y-2"><Label>Contact Person</Label><Input value={editForm.contact_person} onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })} data-testid="edit-client-contact" className="border-slate-300 rounded-sm" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-client-email" className="border-slate-300 rounded-sm" /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-client-phone" className="border-slate-300 rounded-sm" /></div>
              <div className="space-y-2 md:col-span-2"><Label>Address</Label><Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="edit-client-address" className="border-slate-300 rounded-sm" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} data-testid="edit-client-cancel" className="rounded-sm">Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-client-submit" className="bg-primary hover:bg-[#152D53] rounded-sm">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={Boolean(resetTarget)} onOpenChange={(o) => { if (!o) { setResetTarget(null); setNewPassword(""); } }}>
        <DialogContent data-testid="reset-client-password-dialog">
          <DialogHeader><DialogTitle>Reset Password — {resetTarget?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>New Password</Label>
            <Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="reset-client-password-input" placeholder="Minimum 6 characters" className="border-slate-300 rounded-sm" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)} data-testid="reset-client-password-cancel" className="rounded-sm">Cancel</Button>
            <Button onClick={doResetPassword} disabled={saving} data-testid="reset-client-password-submit" className="bg-primary hover:bg-[#152D53] rounded-sm">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={`Permanently delete ${deleteTarget?.company_name}?`}
        description="The client record and its portal login will be permanently deleted. Past orders remain in the system."
        confirmLabel="Delete Permanently"
        destructive
        onConfirm={doDelete}
        testId="delete-client-dialog"
      />

      <ConfirmDialog
        open={Boolean(toggleTarget)}
        onOpenChange={(o) => !o && setToggleTarget(null)}
        title={`${toggleTarget?.status === "active" ? "Deactivate" : "Activate"} ${toggleTarget?.company_name}?`}
        description={toggleTarget?.status === "active"
          ? "The client will no longer be able to log in, and cannot be selected for new orders. Existing orders remain visible to admin."
          : "The client will be able to log in and be selectable for new orders again."}
        confirmLabel={toggleTarget?.status === "active" ? "Deactivate" : "Activate"}
        destructive={toggleTarget?.status === "active"}
        onConfirm={doToggle}
        testId="toggle-client-dialog"
      />
    </div>
  );
}
